import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faSearch } from "@fortawesome/free-solid-svg-icons";
import { getSettlementDeliveryAddressLabels } from "../../../services/settlementApi";

const DELIVERY_LABEL_MAX_CODES = 500;
const DELIVERY_LABEL_CODE_REGEX = /^[A-Za-z0-9_-]+$/;
const DEFAULT_DEPARTMENT = "CREDIT & COLLECTION";
const ROW_ID_FIELD = "__delivery_label_row_id";
const ROW_WAS_MISSING_FIELD = "__delivery_label_was_missing";

const parseDeliveryProviderCodes = (rawValue) => {
  const seen = new Set();
  const validCodes = [];
  const invalidCodes = [];

  String(rawValue ?? "")
    .split(/[\n,;]+/)
    .forEach((item) => {
      const code = item.trim().replace(/^['"]+|['"]+$/g, "");
      if (!code) return;

      if (!DELIVERY_LABEL_CODE_REGEX.test(code)) {
        invalidCodes.push(code);
        return;
      }

      if (!seen.has(code)) {
        seen.add(code);
        validCodes.push(code);
      }
    });

  return { validCodes, invalidCodes };
};

const getDeliveryLabelAddress = (row) =>
  [row.streetno_bldg, row.baranggay, row.city, row.province]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");

const getDeliveryLabelDepartment = (row) => {
  const value = String(row.checkdelivery_departnent ?? "").trim();
  return value || DEFAULT_DEPARTMENT;
};

const getDeliveryLabelContactPerson = (row) =>
  String(row.checkdel_contact_person ?? "").trim();

const getDeliveryLabelProviderName = (row) =>
  String(row.provider_name ?? "").trim().toUpperCase();

const getDeliveryLabelContactNos = (row) =>
  String(row.checkdel_contact_num ?? "").trim();

const isMissingDeliveryLabelRow = (row) => {
  const contactPerson = getDeliveryLabelContactPerson(row);
  const contactNos = getDeliveryLabelContactNos(row);
  const missingContactPerson =
    !contactPerson || contactPerson.toUpperCase() === "UNAVAILABLE";
  const missingContactNos = !contactNos;
  return missingContactPerson || missingContactNos;
};

const getSearchHaystack = (row) =>
  [
    getDeliveryLabelDepartment(row),
    getDeliveryLabelContactPerson(row),
    getDeliveryLabelContactNos(row),
    getDeliveryLabelProviderName(row),
    getDeliveryLabelAddress(row),
  ]
    .join(" ")
    .toLowerCase();

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const buildDeliveryLabelPrintHtml = (rows, fromContactNumber = "") => {
  const normalizedFromContactNumber = String(fromContactNumber ?? "").trim();
  const labelsHtml = rows
    .map((row) => {
      const department = getDeliveryLabelDepartment(row);
      const contactPerson = getDeliveryLabelContactPerson(row);
      const providerName = getDeliveryLabelProviderName(row);
      const address = getDeliveryLabelAddress(row);
      const contactNos = getDeliveryLabelContactNos(row);

      return `
      <div class="label-row">
        <div class="box from-box">
          <div>
            <div class="heading strong">FR: ADVANCED MEDICAL ACCESS PHILIPPINES, INC.<br><span style="margin-left:20px;">(AMAPHIL)</span></div>
            <div class="line-center">Unit 501, Aralco Building J.P. Rizal Street,</div>
            <div class="line-center">Brgy. Poblacion, Makati City</div>
            ${normalizedFromContactNumber ? `<div class="line-center">${escapeHtml(normalizedFromContactNumber)}</div>` : ""}
          </div>
        </div>
        <div class="box to-box">
          <div>
            <div class="to-line strong">${escapeHtml(department)}${contactPerson ? " - " + escapeHtml(contactPerson) : ""}</div>
            <div class="to-line strong">${escapeHtml(providerName)}</div>
            <div class="to-line strong">${escapeHtml(address)}</div>
          </div>
          <div class="strong">${escapeHtml(contactNos)}</div>
        </div>
      </div>`;
    })
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Provider Delivery Address Labels</title>
  <style>
    @page { size: A4 portrait; margin: 10mm; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      margin: 0;
      background: #fff;
      color: #000;
    }
    .sheet {
      width: 190mm;
      margin: 0 auto;
      padding: 0;
    }
    .label-row {
      display: flex;
      gap: 6mm;
      margin-bottom: 4mm;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .box {
      width: 89mm;
      height: 38mm;
      border: 3px double #000;
      box-sizing: border-box;
      padding: 2mm 2.5mm;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      font-size: 10.5px;
      line-height: 1.35;
      overflow: hidden;
    }
    .strong { font-weight: 700; }
    .heading { margin-bottom: 2mm; }
    .to-line { margin-bottom: 2.3mm; }
    .line-center { text-align: center; }
  </style>
</head>
<body>
  <div class="sheet">
    ${labelsHtml || "<p>No labels available.</p>"}
  </div>
</body>
</html>`;
};

const ProviderDeliveryAddressLabelGenerator = ({
  initialProviderCodes = "",
  initialRows = [],
  initialStatus = "",
  initialFromContactNumber = "",
  onStateChange,
  rootClassName = "",
  controlsClassName = "p-4 border-b bg-gray-50",
  previewClassName = "p-4 bg-white min-h-[420px]",
}) => {
  const rowIdCounterRef = useRef(0);

  const withRowIds = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const existingId = String(row?.[ROW_ID_FIELD] ?? "").trim();
      const existingWasMissing = row?.[ROW_WAS_MISSING_FIELD];
      const wasMissing =
        typeof existingWasMissing === "boolean"
          ? existingWasMissing
          : isMissingDeliveryLabelRow(row);

      if (existingId) {
        return {
          ...row,
          [ROW_ID_FIELD]: existingId,
          [ROW_WAS_MISSING_FIELD]: wasMissing,
        };
      }

      rowIdCounterRef.current += 1;
      return {
        ...row,
        [ROW_ID_FIELD]: `delivery-label-${rowIdCounterRef.current}`,
        [ROW_WAS_MISSING_FIELD]: wasMissing,
      };
    });
  }, []);

  const [deliveryLabelProviderCodes, setDeliveryLabelProviderCodes] = useState(
    () => String(initialProviderCodes ?? "")
  );
  const [deliveryLabelRows, setDeliveryLabelRows] = useState(() =>
    withRowIds(initialRows)
  );
  const [deliveryLabelStatus, setDeliveryLabelStatus] = useState(() => {
    const status = String(initialStatus ?? "").trim();
    if (status) return status;

    const rowCount = Array.isArray(initialRows) ? initialRows.length : 0;
    return rowCount > 0
      ? `Loaded ${rowCount} label${rowCount > 1 ? "s" : ""}.`
      : "";
  });
  const [fromContactNumber, setFromContactNumber] = useState(() =>
    String(initialFromContactNumber ?? "").trim()
  );
  const [deliveryLabelLoading, setDeliveryLabelLoading] = useState(false);
  const [filterOnlyMissingContact, setFilterOnlyMissingContact] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (typeof onStateChange !== "function") return;

    onStateChange({
      providerCodes: deliveryLabelProviderCodes,
      rows: deliveryLabelRows,
      status: deliveryLabelStatus,
      fromContactNumber,
    });
  }, [
    deliveryLabelProviderCodes,
    deliveryLabelRows,
    deliveryLabelStatus,
    fromContactNumber,
    onStateChange,
  ]);

  const missingContactCount = useMemo(
    () => deliveryLabelRows.filter((row) => isMissingDeliveryLabelRow(row)).length,
    [deliveryLabelRows]
  );

  const filteredRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return deliveryLabelRows.filter((row) => {
      const isCurrentlyMissing = isMissingDeliveryLabelRow(row);
      const wasDetectedMissing = Boolean(row?.[ROW_WAS_MISSING_FIELD]);

      if (
        filterOnlyMissingContact &&
        !isCurrentlyMissing &&
        !wasDetectedMissing
      ) {
        return false;
      }

      if (!normalizedQuery) return true;
      return getSearchHaystack(row).includes(normalizedQuery);
    });
  }, [deliveryLabelRows, filterOnlyMissingContact, searchQuery]);

  const handleLoadDeliveryLabels = async () => {
    const { validCodes, invalidCodes } = parseDeliveryProviderCodes(
      deliveryLabelProviderCodes
    );

    if (invalidCodes.length > 0) {
      const preview = invalidCodes.slice(0, 8).join(", ");
      toast.warn(
        `Invalid provider code format: ${preview}${
          invalidCodes.length > 8 ? ", ..." : ""
        }`
      );
      return;
    }

    if (validCodes.length > DELIVERY_LABEL_MAX_CODES) {
      toast.warn(
        `Maximum ${DELIVERY_LABEL_MAX_CODES} provider codes allowed.`
      );
      return;
    }

    setDeliveryLabelLoading(true);
    setDeliveryLabelStatus("Loading labels...");
    setDeliveryLabelRows([]);

    try {
      const params =
        validCodes.length > 0 ? { provider_codes: validCodes.join(",") } : {};
      const res = await getSettlementDeliveryAddressLabels(params);
      const payload = res?.data ?? {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      const nextFromContactNumber = String(
        payload.from_contact_number ?? payload.settings?.from_contact_number ?? ""
      ).trim();

      if (payload.success === false) {
        setDeliveryLabelStatus(payload.message || "Failed to load labels.");
        setDeliveryLabelRows([]);
        return;
      }

      setFromContactNumber(nextFromContactNumber);
      setDeliveryLabelRows(withRowIds(rows));
      setDeliveryLabelStatus(
        rows.length > 0
          ? `Loaded ${rows.length} label${rows.length > 1 ? "s" : ""}.`
          : "No records found."
      );
    } catch (err) {
      const invalidFromApi = err?.response?.data?.invalid_provider_codes;
      const apiMessage = err?.response?.data?.message;

      if (Array.isArray(invalidFromApi) && invalidFromApi.length > 0) {
        const preview = invalidFromApi.slice(0, 8).join(", ");
        toast.error(
          `Invalid provider code format: ${preview}${
            invalidFromApi.length > 8 ? ", ..." : ""
          }`
        );
      } else {
        toast.error(
          typeof apiMessage === "string"
            ? apiMessage
            : "Failed to load delivery address labels."
        );
      }

      setDeliveryLabelStatus("Failed to load labels.");
      setDeliveryLabelRows([]);
    } finally {
      setDeliveryLabelLoading(false);
    }
  };

  const handlePrintRows = (rowsToPrint, modeLabel) => {
    if (!rowsToPrint.length) {
      toast.warn(`No ${modeLabel} labels to print.`);
      return;
    }

    const printWindow = window.open("", "_blank", "width=1100,height=900");
    if (!printWindow) {
      toast.error("Popup blocked. Please allow popups to print labels.");
      return;
    }

    printWindow.document.write(
      buildDeliveryLabelPrintHtml(rowsToPrint, fromContactNumber)
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDeliveryLabelFieldChange = (rowId, field, value) => {
    setDeliveryLabelRows((prev) =>
      prev.map((row) => (row[ROW_ID_FIELD] === rowId ? { ...row, [field]: value } : row))
    );
  };

  const hasRows = deliveryLabelRows.length > 0;
  const showingFilteredRows = hasRows && filteredRows.length !== deliveryLabelRows.length;

  return (
    <div className={rootClassName}>
      <div className={controlsClassName}>
        {missingContactCount > 0 && (
          <div className="mb-3 rounded border border-red-300 bg-red-100 px-3 py-2 text-xs text-red-800">
            Missing contact detected: <strong>{missingContactCount}</strong>{" "}
            label{missingContactCount > 1 ? "s" : ""} have unavailable/blank
            contact person or missing contact number.
          </div>
        )}

        <label className="block text-xs font-medium text-gray-600 mb-1">
          Provider Codes (comma or newline separated, max{" "}
          {DELIVERY_LABEL_MAX_CODES})
        </label>
        <textarea
          value={deliveryLabelProviderCodes}
          onChange={(e) => setDeliveryLabelProviderCodes(e.target.value)}
          placeholder="e.g. 000001,000002"
          className="w-full border rounded px-2 py-1.5 text-sm h-24 focus:outline-none focus:ring-1 focus:ring-blue-400"
        />

        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={filterOnlyMissingContact}
              onChange={(e) => setFilterOnlyMissingContact(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Filter Only Missing Contact
          </label>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search department, contact person/no., provider, or address"
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            onClick={handleLoadDeliveryLabels}
            disabled={deliveryLabelLoading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faSearch} />
            {deliveryLabelLoading ? "Loading..." : "Load & Preview"}
          </button>

          <button
            onClick={() => handlePrintRows(deliveryLabelRows, "all")}
            disabled={!hasRows}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faPrint} />
            Print Labels ALL
          </button>

          <button
            onClick={() => handlePrintRows(filteredRows, "filtered")}
            disabled={filteredRows.length === 0}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-60"
          >
            <FontAwesomeIcon icon={faPrint} />
            Print Labels ONLY FILTERED
          </button>

          <span className="text-xs text-gray-600">{deliveryLabelStatus}</span>
          {hasRows && (
            <span className="text-xs text-gray-600">
              Showing {filteredRows.length} of {deliveryLabelRows.length}
            </span>
          )}
          {showingFilteredRows && (
            <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
              Filter/search is active
            </span>
          )}
        </div>
      </div>

      <div className={previewClassName}>
        {!hasRows ? (
          <p className="text-sm text-gray-500">
            Enter provider codes then click <strong>Load &amp; Preview</strong>.
          </p>
        ) : filteredRows.length === 0 ? (
          <p className="text-sm text-gray-500">
            No labels match your current filter/search.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <div className="space-y-3 min-w-[760px]">
              {filteredRows.map((row) => {
                const rowId = row[ROW_ID_FIELD];
                const department = getDeliveryLabelDepartment(row);
                const contactPerson = getDeliveryLabelContactPerson(row);
                const providerName = getDeliveryLabelProviderName(row);
                const address = getDeliveryLabelAddress(row);
                const contactNos = getDeliveryLabelContactNos(row);

                return (
                  <div
                    key={rowId}
                    className="flex gap-3"
                    style={{ breakInside: "avoid" }}
                  >
                    <div className="border-2 border-double border-black p-2 box-border w-[3.5in] h-[1.5in] flex flex-col justify-between text-[11px] leading-[1.35]">
                      <div>
                        <div className="font-bold mb-2">
                          FR: ADVANCED MEDICAL ACCESS PHILIPPINES, INC.
                          <br />
                          <span className="ml-5">(AMAPHIL)</span>
                        </div>
                        <div className="text-center">
                          Unit 501, Aralco Building J.P. Rizal Street,
                        </div>
                        <div className="text-center">
                          Brgy. Poblacion, Makati City
                        </div>
                        {fromContactNumber && (
                          <div className="text-center">
                            {fromContactNumber}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-2 border-double border-black p-2 box-border w-[3.5in] h-[1.5in] flex flex-col justify-between text-[11px] leading-[1.35]">
                      <div>
                        <div className="my-1 space-y-1">
                          <input
                            type="text"
                            value={department}
                            onChange={(e) =>
                              handleDeliveryLabelFieldChange(
                                rowId,
                                "checkdelivery_departnent",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] font-bold"
                            placeholder="Department"
                          />
                          <input
                            type="text"
                            value={contactPerson}
                            onChange={(e) =>
                              handleDeliveryLabelFieldChange(
                                rowId,
                                "checkdel_contact_person",
                                e.target.value
                              )
                            }
                            className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] font-bold"
                            placeholder="Contact Person"
                          />
                        </div>
                        <div className="font-bold my-1">{providerName}</div>
                        <div className="font-bold my-1">{address}</div>
                      </div>

                      <input
                        type="text"
                        value={contactNos}
                        onChange={(e) =>
                          handleDeliveryLabelFieldChange(
                            rowId,
                            "checkdel_contact_num",
                            e.target.value
                          )
                        }
                        className="w-full border border-gray-300 rounded px-1 py-0.5 text-[11px] font-bold"
                        placeholder="Contact Nos"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderDeliveryAddressLabelGenerator;
